import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styles from "./HatTooltip.module.css";
import { Text } from "@carbon6/c6-frontend-design";
import { hatcheryTextModes } from "@/app/theme";

export type HatTooltipPlacement = "top" | "bottom" | "left" | "right";
// 트리거를 기준으로 버블이 커지는 방향(top/bottom 전용).
//   - center: 트리거 중앙에 맞춘다.
//   - end: 오른쪽 끝을 맞추고 **왼쪽으로** 커진다.
// end 는 표 오른쪽 끝 셀처럼 오른쪽에 공간이 없는 자리에 쓴다. 그런 자리에서 뷰포트 clamp 에
// 의존하면 트리거와 버블의 위치 관계가 바뀐다. 화살표는 모든 정렬에서 트리거 중앙을 가리킨다.
// 그래서 end 에서는 화살표가 버블 우측(위/아래 모서리)에 있다.
export type HatTooltipAlign = "center" | "start" | "end";
// 툴팁 색은 text 하나로 통일한다. mode 는 트리거 sizing 만 정한다.
//   - text: 콘텐츠 폭(tight).
//   - plain: 전폭(full-width row/nav 트리거용).
export type HatTooltipMode = "text" | "plain";

export type HatTooltipProps = {
  content: ReactNode;
  children: ReactNode;
  placement?: HatTooltipPlacement;
  align?: HatTooltipAlign;
  disabled?: boolean;
  mode?: HatTooltipMode;
};

export type HatTextTooltipProps = Omit<HatTooltipProps, "content"> & {
  text: string;
};

// 세 간격:
//   - 버블과 트리거 사이 간격. 작게 두어 화살표·버블이 트리거에 붙어 보이게 한다.
//   - 뷰포트 가장자리의 최소 여백.
//   - 화살표가 버블 끝에 붙지 않게 하는 여백.
const tooltipOffset = 5;
const viewportMargin = 8;
const arrowEdgeMargin = 12;

// 측정 후 확정한 버블 위치. left/top 은 버블 좌상단(px, fixed 기준)이다. arrowLeft/arrowTop 은
// 화살표가 트리거를 가리키게 하는 버블 안 오프셋(px)이다. placement 축에 맞는 값 하나만 채운다.
type TooltipLayout = {
  left: number;
  top: number;
  arrowLeft?: number;
  arrowTop?: number;
};

export function HatTooltip({
  content,
  children,
  placement = "top",
  align = "center",
  disabled = false,
  mode = "text",
}: HatTooltipProps) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  // null = 아직 측정 전. 버블을 자연 폭으로 화면 밖에 숨겨 렌더한다. 그 뒤 useLayoutEffect 가
  // 크기를 재고 위치를 확정한다.
  const [layout, setLayout] = useState<TooltipLayout | null>(null);
  const hasContent =
    content !== null && content !== undefined && content !== false && content !== "";
  const isVisible = !disabled && hasContent && visible;

  const show = useCallback(() => {
    if (disabled || !hasContent) {
      return;
    }
    setLayout(null);
    setVisible(true);
  }, [disabled, hasContent]);

  const hide = useCallback(() => {
    setVisible(false);
    setLayout(null);
  }, []);

  // 버블이 나타나거나 내용/placement 이 바뀌면 실제 크기를 잰다. 그리고 좌표를 뷰포트 안으로
  // clamp 하고, 화살표가 트리거를 가리키도록 오프셋을 정한다. useLayoutEffect 는 paint 전에
  // 실행된다. 그래서 깜빡임이 없다.
  useLayoutEffect(() => {
    if (!isVisible) {
      return;
    }
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) {
      return;
    }
    setLayout(
      computeLayout(
        trigger.getBoundingClientRect(),
        tooltip.getBoundingClientRect(),
        placement,
        align,
      ),
    );
  }, [isVisible, placement, align, content]);

  const trigger = (
    <span
      ref={triggerRef}
      className={[
        styles.trigger,
        resolveTriggerModeClassName(mode),
        disabled || !hasContent ? styles.triggerDisabled : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-describedby={isVisible ? tooltipId : undefined}
      onFocus={show}
      onBlur={hide}
      onPointerEnter={show}
      onPointerLeave={hide}
    >
      {children}
    </span>
  );

  if (!isVisible || typeof document === "undefined") {
    return trigger;
  }

  return (
    <>
      {trigger}
      {createPortal(
        // 버블을 body 에 fixed 로 표시한다. 트리거 안에 그리면 DataTable cell 의
        // overflow:hidden(ellipsis)이 버블을 자른다. 측정 전(layout=null)에는 좌상단에 자연 폭으로
        // 숨긴다. 측정 후 확정 좌표로 옮긴다.
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className={[styles.tooltip, styles.textTooltip, styles[placement]]
            .filter(Boolean)
            .join(" ")}
          style={
            layout
              ? { left: layout.left, top: layout.top }
              : { left: 0, top: 0, visibility: "hidden" }
          }
        >
          {content}
          <span className={styles.arrow} style={resolveArrowStyle(layout)} aria-hidden="true" />
        </div>,
        document.body,
      )}
    </>
  );
}

export function HatTextTooltip({ text, mode = "text", ...tooltipProps }: HatTextTooltipProps) {
  return (
    <HatTooltip
      {...tooltipProps}
      mode={mode}
      content={
        <Text variant={hatcheryTextModes["caption-bold"]} className={styles.textContent}>
          {text}
        </Text>
      }
    />
  );
}

function resolveTriggerModeClassName(mode: HatTooltipMode): string {
  switch (mode) {
    case "text":
      return styles.textTrigger;
    case "plain":
      return styles.plainTrigger;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// 트리거·버블 rect 로 뷰포트 안에 들어오는 버블 좌상단과 화살표 오프셋을 계산한다.
//   - top/bottom 은 가로를, left/right 는 세로를 트리거 중심 기준으로 clamp 한다.
//   - 부착 축(top/bottom 의 세로, left/right 의 가로)도 같은 방식으로 clamp 한다. 트리거가
//     뷰포트 가장자리에 붙어 있으면 자연 위치가 화면 밖으로 나갈 수 있다.
//   - 화살표는 트리거 중앙을 가리킨다. 단, 버블 가장자리 여백 안으로 clamp 한다.
// 트리거가 오른쪽 가장자리에 있으면 버블은 왼쪽으로 이동하고 화살표는 우측 하단에 있다
// (top-left + 우하 화살표).
function computeLayout(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  placement: HatTooltipPlacement,
  align: HatTooltipAlign,
): TooltipLayout {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tw = tooltipRect.width;
  const th = tooltipRect.height;

  if (placement === "top" || placement === "bottom") {
    const centerX = triggerRect.left + triggerRect.width / 2;
    const left = clamp(
      alignedLeft(triggerRect, tw, align, centerX),
      viewportMargin,
      Math.max(viewportMargin, vw - tw - viewportMargin),
    );
    const naturalTop =
      placement === "top"
        ? triggerRect.top - th - tooltipOffset
        : triggerRect.bottom + tooltipOffset;
    const top = clamp(
      naturalTop,
      viewportMargin,
      Math.max(viewportMargin, vh - th - viewportMargin),
    );
    const arrowLeft = clamp(
      centerX - left,
      arrowEdgeMargin,
      Math.max(arrowEdgeMargin, tw - arrowEdgeMargin),
    );
    return { left, top, arrowLeft };
  }

  const centerY = triggerRect.top + triggerRect.height / 2;
  const top = clamp(
    centerY - th / 2,
    viewportMargin,
    Math.max(viewportMargin, vh - th - viewportMargin),
  );
  const left =
    placement === "left"
      ? triggerRect.left - tw - tooltipOffset
      : triggerRect.right + tooltipOffset;
  const arrowTop = clamp(
    centerY - top,
    arrowEdgeMargin,
    Math.max(arrowEdgeMargin, th - arrowEdgeMargin),
  );
  return { left, top, arrowTop };
}

// 정렬이 정하는 버블 좌변(clamp 전). end 는 오른쪽 끝을 트리거 오른쪽에 맞추고 왼쪽으로
// 커진다. start 는 그 반대다. 세로축(left/right placement)에는 적용하지 않는다. 그 축에서
// 같은 문제가 있는 자리가 아직 없다.
function alignedLeft(
  triggerRect: DOMRect,
  tooltipWidth: number,
  align: HatTooltipAlign,
  centerX: number,
): number {
  switch (align) {
    case "start":
      return triggerRect.left;
    case "end":
      return triggerRect.right - tooltipWidth;
    case "center":
      return centerX - tooltipWidth / 2;
  }
}

// 화살표 인라인 위치. placement 축의 CSS 기본값(50%)을 덮어써서 화살표가 트리거를 가리키게 한다.
function resolveArrowStyle(layout: TooltipLayout | null): CSSProperties | undefined {
  if (!layout) {
    return undefined;
  }
  if (layout.arrowLeft !== undefined) {
    return { left: layout.arrowLeft };
  }
  if (layout.arrowTop !== undefined) {
    return { top: layout.arrowTop };
  }
  return undefined;
}

/**
 * 꺼진 버튼의 사유 툴팁.
 *
 * disabled 버튼은 포인터 이벤트를 받지 못해 툴팁이 뜨지 않는다. 그래서 툴팁을 래퍼에 연결하고 버튼의
 * 포인터 이벤트를 끈다. 상태 때문에 못 하는 동작은 버튼을 숨기지 않고 끈 뒤 이 툴팁으로 사유를 알린다.
 */
export function DisabledReasonTooltip({
  text,
  children,
}: {
  text: string;
  children: ReactNode;
}): ReactNode {
  return (
    <HatTextTooltip text={text}>
      <span className={styles.disabledReasonTrigger}>{children}</span>
    </HatTextTooltip>
  );
}
